import {DataStatus} from 'platform/components';
import {Hide, VStack} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {useState} from 'react';

import {descend, isEmpty, isNotNil, uniqBy} from 'ramda';

import {
  useLazyGetServiceAlbumListQuery,
  useLazyGetServiceAlbumPhotoListQuery,
  useLazyGetVehiclePhotosQuery,
  useLazyListVehicleAlbumsQuery,
} from '@dms/api/sales';
import {PartialVehiclePhotosRoutes, VehicleAlbums} from '@dms/api/shared';
import {useLazyGetMetadaServiceCaseQuery} from '@dms/api/workshop';

import {useOnMount} from 'shared/web';

import {useBatchDownload} from '../../hooks/useBatchDownload';
import {getGridServiceAlbumPhotos} from '../../utils/getGridPhotos';
import {DownloadDialog} from './components/DownloadDialog';
import {
  ServiceCasePhotosCard,
  ServiceCasePhotosCardProps,
} from './components/ServiceCasePhotosCard';

interface ServiceCasePhotosProps {
  vehicleId: string;
  ignoredServiceCaseId?: string | Nullish;
  hideWhenEmpty?: boolean;
}

type HistoricalServiceCaseAlbum = {
  id: string;
  serviceCaseId: string;
  createdAt: string;
  source: 'service-album' | 'vehicle-album';
};

export function ServiceCasePhotos(props: ServiceCasePhotosProps) {
  const [serviceCaseCards, setServiceCaseCards] = useState<ServiceCasePhotosCardProps[]>([]);
  const [isPhotosLoading, setIsPhotosLoading] = useState(true);
  const [isPhotosError, setIsPhotosError] = useState(false);

  const {handleDownload, eventData, isLoading, isStatePending, handleServiceAlbumDownload} =
    useBatchDownload();

  const [lazyGetServiceAlbumList] = useLazyGetServiceAlbumListQuery();

  const [lazyGetServiceAlbumPhotoList] = useLazyGetServiceAlbumPhotoListQuery();
  const [lazyListVehicleAlbums] = useLazyListVehicleAlbumsQuery();
  const [lazyGetVehiclePhotos] = useLazyGetVehiclePhotosQuery();

  const [lazyGetMetadaServiceCase] = useLazyGetMetadaServiceCaseQuery();

  const handleBatchDownload = (albumId: string) =>
    handleServiceAlbumDownload({
      vehicleId: props.vehicleId,
      serviceAlbumId: albumId,
    });

  const handleVehicleAlbumBatchDownload = (albumId: string) =>
    handleDownload({
      vehiclePhotoLocation: PartialVehiclePhotosRoutes.vehicle,
      vehicleId: props.vehicleId,
      albumId,
    });

  useOnMount(() => {
    (async () => {
      if (!props.vehicleId) {
        return;
      }

      const [serviceCaseAlbums, vehicleAlbums, vehiclePhotos] = await Promise.all([
        lazyGetServiceAlbumList({
          vehicleId: props.vehicleId,
        }).unwrap(),
        lazyListVehicleAlbums({
          vehicleId: props.vehicleId,
        }).unwrap(),
        lazyGetVehiclePhotos({
          vehicleId: props.vehicleId,
        }).unwrap(),
      ]);

      const albums: HistoricalServiceCaseAlbum[] = [
        ...(serviceCaseAlbums ?? []).map((album) => ({
          id: album.id,
          serviceCaseId: album.serviceCaseId,
          createdAt: album.createdAt,
          source: 'service-album' as const,
        })),
        ...(vehicleAlbums ?? []).flatMap((album) =>
          album.name === VehicleAlbums.PHOTOS_SERVICE_CASE && album.serviceCaseId !== null
            ? [
                {
                  id: album.id,
                  serviceCaseId: album.serviceCaseId,
                  createdAt: album.createdAt,
                  source: 'vehicle-album' as const,
                },
              ]
            : []
        ),
      ]
        .filter(
          (item): item is HistoricalServiceCaseAlbum =>
            isNotNil(item.serviceCaseId) && item.serviceCaseId !== props.ignoredServiceCaseId
        )
        .toSorted(descend((album) => album.createdAt));

      const serviceCaseAlbumGroups = Object.values(
        albums.reduce<Record<string, HistoricalServiceCaseAlbum[]>>((acc, album) => {
          acc[album.serviceCaseId] = [...(acc[album.serviceCaseId] ?? []), album];
          return acc;
        }, {})
      ).toSorted(descend((albumGroup) => albumGroup[0]?.createdAt ?? ''));

      const promises = serviceCaseAlbumGroups.map(async (albumGroup) => {
        const firstAlbum = albumGroup[0];
        if (!firstAlbum) {
          return;
        }

        const serviceCaseId = firstAlbum.serviceCaseId;
        const photos = await Promise.all(
          albumGroup.map((album) =>
            album.source === 'service-album'
              ? lazyGetServiceAlbumPhotoList({
                  vehicleId: props.vehicleId,
                  serviceAlbumId: album.id,
                }).unwrap()
              : Promise.resolve(vehiclePhotos?.filter((photo) => photo.albumId === album.id))
          )
        );

        const serviceCasePhotos = uniqBy(
          (photo) => photo.url,
          getGridServiceAlbumPhotos(photos.flat())
        );

        if (isEmpty(serviceCasePhotos)) {
          return null;
        }

        const serviceCase = await lazyGetMetadaServiceCase({
          serviceCaseId,
        }).unwrap();

        return {
          serviceCase,
          serviceCasePhotos,
          checkinPhotos: [],
          checkinVideos: [],
          handoverPhotos: [],
          handoverVideos: [],
          onDownloadAll: () => {
            albumGroup.forEach((album) => {
              if (album.source === 'service-album') {
                handleBatchDownload(album.id);
                return;
              }

              handleVehicleAlbumBatchDownload(album.id);
            });
          },
          onDownloadCheckin: () => undefined,
          onDownloadHandover: () => undefined,
        };
      });

      Promise.allSettled(promises ?? [])
        .then((data) => {
          const cards = data.reduce<ServiceCasePhotosCardProps[]>((acc, card) => {
            if (card.status === 'fulfilled' && isNotNil(card.value)) {
              return [...acc, card.value];
            }
            return acc;
          }, []);

          setServiceCaseCards(cards);
          setIsPhotosLoading(false);
        })
        .catch(() => {
          setIsPhotosError(true);
        });
    })();
  });

  return (
    <Hide when={props.hideWhenEmpty && !isPhotosLoading && isEmpty(serviceCaseCards)}>
      <DataStatus
        isLoading={isPhotosLoading}
        isError={isPhotosError}
        isEmpty={isEmpty(serviceCaseCards)}
      >
        <VStack spacing={4}>
          {serviceCaseCards.map((card, index) => (
            <ServiceCasePhotosCard
              key={`${card.serviceCase?.id ?? 'service-case'}-${index}`}
              {...card}
            />
          ))}
        </VStack>
      </DataStatus>
      <DownloadDialog
        withWrapper
        isOpen={isLoading || isStatePending}
        fileName={eventData?.data?.bulkName}
        fileCount={eventData?.data?.objects.length}
      />
    </Hide>
  );
}
